import Vue from 'vue';
import { Component, Watch, Prop } from 'vue-property-decorator';
import moment from 'moment';

import { OrderWithContext, OrderDeliveryInfo, Restaurant, OrderDeliveryInfoStatus, DeliveryEstimate } from '@/models';
import { Routes } from '@/router';
import { FeedbackResponse } from '@/models/feedback';

const REFRESH_ORDER_POLL_MS = 60 * 1000;
const MAX_ORDER_POLL_TIME_MS = 3 * 60 * 60 * 1000;

@Component({
  name: 'CmgOrderStatusBar',
})
export default class CmgOrderStatusBar extends Vue {
  refreshOrderInterval: number | null = null;
  refreshOrderStartTime: Date | null = null;
  lastCheckedTime: moment.Moment = moment();

  delivery: OrderDeliveryInfo | null = null;
  restaurant: Restaurant | null = null;
  isOrderComplete = false;

  @Prop({ default: false, type: Boolean })
  blur!: boolean;

  @Prop({ required: true, type: Boolean })
  visible!: boolean;

  // This may have odd side affects for locations that border time zones
  get isExpired(): boolean {
    if (this.isDelivery) {
      return !!this.delivery && moment(this.delivery.quotedDeliveryTime || '').add(3, 'hour') < this.lastCheckedTime;
    }

    return !!this.order && moment(this.order.order.pickupDateTimeLocal || '').add(3, 'hour') < this.lastCheckedTime;
  }

  get formattedPickupTime() {
    if (!this.order) {
      return null;
    }

    return moment(this.order.order.pickupDateTimeLocal || '').format('h:mma');
  }

  get isDelivery(): boolean {
    return !!this.order && !!this.order.order.delivery;
  }

  get order() {
    return this.$appStore.state.order.lastOrder.order;
  }

  get deliveryEstimate(): DeliveryEstimate | null {
    return this.$appStore.state.order.lastOrder.estimate;
  }

  get deliveryAddress(): string {
    if (!this.delivery) {
      return '';
    }
    
    const address = this.delivery.dropOffAddress;
    return `${address.addressLine1}, ${address.locality} ${address.administrativeArea} ${address.postalCode}`;
  }

  get formattedArrivalInfo(): string {
    if (!this.deliveryEstimate) {
      return '';
    }

    return `${this.deliveryEstimate.minDeliveryTime} to ${this.deliveryEstimate.maxDeliveryTime}`;
  }

  get restaurantAddress(): string {
    if (!this.restaurant) {
      return '';
    }

    const address = this.restaurant.address;
    return `${address.addressLine1}, ${address.locality} ${address.administrativeArea} ${address.postalCode}`;
  }

  get ratingStarEmpty(): string {
    return this.$appStore.state.cms.images.icons.ratingStarWhite.default;
  }

  get feedback(): FeedbackResponse | null {
    return this.$appStore.state.order.feedback;
  }

  @Watch('order', { immediate: true, deep: true })
  onOrderUpdated(order: OrderWithContext | null) {
    const delivery = order && order.order.delivery;
    if (!delivery) {
      this.delivery = null;
    }

    if (this.refreshOrderInterval) {
      window.clearInterval(this.refreshOrderInterval);
    }

    this.refreshOrder();
    this.refreshOrderStartTime = new Date();
    this.refreshOrderInterval = window.setInterval(() => {
      const timeDiff = new Date().getTime() - this.refreshOrderStartTime!.getTime();
      if (timeDiff > MAX_ORDER_POLL_TIME_MS) {
        this.clearRefreshOrderInterval();
      }
      if (this.visible) {
        this.refreshOrder();
      }
    }, REFRESH_ORDER_POLL_MS);
  }

  @Watch('isExpired', { immediate: true })
  onIsExpiredUpdated(expired: boolean) {
    if (expired) {
      this.$services.order.setLastOrder(null, null);
      this.clearRefreshOrderInterval();
    }
  }

  async mounted() {
    if (this.order && !this.feedback) {
      try {
        const feedbackResponse = await this.$services.feedback.getFeedback(this.order.order.orderId);
        this.$services.feedback.setFeedback(feedbackResponse);
      } catch (e) {
        this.$services.logger.error('Error submitting feedback', e);
      }
    }
  }

  destroyed() {
    this.clearRefreshOrderInterval();
  }

  clearRefreshOrderInterval() {
    if (this.refreshOrderInterval) {
      clearInterval(this.refreshOrderInterval);
    }
  }

  onClickGetOrderDetails() {
    if (!this.order) {
      return;
    }

    this.$router.push({ name: Routes.OrderConfirmation, params: { orderId: this.order.order.orderId } });
  }

  onClickRate(event: Event) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('feedback', true);
    event.stopPropagation();
  }

  onClickStatusBar() {
    if (!this.order) {
      return;
    }

    this.$router.push({ name: Routes.OrderConfirmation, params: { orderId: this.order.order.orderId } });
  }

  private async refreshOrder() {
    if (!this.order) {
      return;
    }

    this.lastCheckedTime = moment();

    if (this.isDelivery) {
      const delivery = this.order.order.delivery;
      if (!delivery) {
        return;
      }

      this.delivery = await this.$services.delivery.getDeliveryInfo(delivery.deliveryId);
      this.isOrderComplete = this.delivery.deliveryStatus == OrderDeliveryInfoStatus.Delivered;
    } else {
      const now = moment();
      this.isOrderComplete = moment(this.order.order.pickupDateTimeLocal) <= now;

      if (this.restaurant) {
        return;
      }

      this.restaurant = await this.$services.restaurant.getRestaurantByNumber(this.order.order.restaurantId);
    }
  }
}
